import { create } from 'zustand';

/**
 * Принт с питомцем, который человек собирает сам.
 *
 * Состояние отдельным хранилищем, а не внутри экрана: снимок и надписи
 * нужны и на шаге основы, и на шаге принта, и на последнем экране,
 * и в заказе. Внутри экрана они жили бы ровно до перехода.
 *
 * Снимок хранится ссылкой на объект в памяти вкладки, а не строкой base64:
 * файл с телефона весит мегабайты, и в хранилище браузера он не помещается.
 * Поэтому обновление страницы сбрасывает сборку — осознанный размен,
 * пока нет загрузки на сервер.
 */
interface PetState {
  /** Ссылка на загруженный снимок. Пусто — человек ещё не выбрал файл. */
  photoUrl: string | null;
  /**
   * Тот же снимок без фона. Приходит с сервера через несколько секунд
   * после загрузки.
   *
   * Исходник при этом остаётся: фон человек может вернуть, и просить
   * загрузить снимок заново ради этого было бы издевательством.
   */
  cutoutUrl: string | null;
  /**
   * Тот же вырезанный снимок в полном размере — то, что уйдёт в печать.
   *
   * Отдельно от экранного намеренно. На экране нужна копия поменьше:
   * снимок с телефона весит мегабайты, и тянуть их ради картинки
   * на треть экрана незачем. В печати нужен ровно полный размер.
   * Один адрес на оба случая означал бы либо тяжёлый экран, либо
   * непригодный к печати заказ — а именно это и было.
   */
  cutoutPrintUrl: string | null;
  /** Идёт вырезание. Нужно, чтобы показать ожидание, а не пустоту. */
  isCutting: boolean;
  /**
   * Вырезание не удалось или не настроено. Снимок при этом остаётся
   * с фоном и работает: человек теряет улучшение, а не возможность
   * собрать вещь.
   */
  cutFailed: boolean;
  name: string;
  year: string;
  /** Свободные строки, которые человек добавил сам. */
  lines: string[];

  /** Подложка под принтом. В макете это галочка «фон». */
  hasBackground: boolean;
  /** Ключ цвета надписи из палитры изделия. */
  inkColor: string;
  /** Ключ шрифта надписи. */
  font: string;

  setPhoto: (file: File) => void;
  setCutout: (cutout: { url: string; printUrl: string } | null) => void;
  setCutting: (value: boolean) => void;
  setCutFailed: (value: boolean) => void;
  setName: (value: string) => void;
  setYear: (value: string) => void;
  setLine: (index: number, value: string) => void;
  addLine: () => void;
  setBackground: (value: boolean) => void;
  setInk: (value: string) => void;
  setFont: (value: string) => void;
  reset: () => void;
}

/** Имя длиннее не поместится на принт читаемым кеглем. */
export const MAX_NAME = 14;

/** Сколько свободных строк человек может добавить сверх имени и года. */
export const MAX_LINES = 3;

export const usePetStore = create<PetState>()((set, get) => ({
  photoUrl: null,
  cutoutUrl: null,
  cutoutPrintUrl: null,
  isCutting: false,
  cutFailed: false,
  name: '',
  year: '',
  lines: [''],

  // Фона по умолчанию нет: снимок приходит вырезанным, и на футболке
  // питомец стоит сам по себе. Вернуть прямоугольник с комнатой человек
  // может галочкой — но по умолчанию её быть не должно, иначе половина
  // заказов уедет в печать с кухней на груди.
  hasBackground: false,
  inkColor: 'accent',

  // Первым показываем шрифт1. Шрифт2 — рукописный Caveat, он заметно уже
  // остальных и на первом показе задаёт слишком лёгкое настроение для
  // вещи, которую человек видит впервые. Выбрать его он по-прежнему может.
  font: 'font1',

  setPhoto: (file) => {
    // Прежнюю ссылку освобождаем: каждая держит файл в памяти вкладки,
    // и человек, перебравший десяток снимков, оставил бы там все.
    const previous = get().photoUrl;
    if (previous) URL.revokeObjectURL(previous);

    // Вырезанный снимок прошлого файла тоже забываем: он принадлежал
    // другому питомцу и, оставшись, подменил бы новый.
    set({
      photoUrl: URL.createObjectURL(file),
      cutoutUrl: null,
      cutoutPrintUrl: null,
      isCutting: false,
      cutFailed: false,
      // Новый снимок — снова без фона. Галочка «вернуть фон», поставленная
      // для прошлого фото, переезжала на новое, и вырезанный питомец
      // показывался с фоном.
      hasBackground: false,
    });
  },

  setCutout: (cutout) =>
    set({
      cutoutUrl: cutout?.url ?? null,
      cutoutPrintUrl: cutout?.printUrl ?? null,
      isCutting: false,
      cutFailed: false,
    }),
  setCutting: (value) => set({ isCutting: value }),
  setCutFailed: (value) => set({ cutFailed: value, isCutting: false }),

  setName: (value) => set({ name: value.slice(0, MAX_NAME) }),

  // В поле года держим только цифры: маска сложнее, а ошибиться проще.
  setYear: (value) => set({ year: value.replace(/\D/g, '').slice(0, 4) }),

  setLine: (index, value) =>
    set((state) => {
      const lines = [...state.lines];
      lines[index] = value.slice(0, 24);
      return { lines };
    }),

  addLine: () =>
    set((state) => (state.lines.length >= MAX_LINES ? state : { lines: [...state.lines, ''] })),

  setBackground: (value) => set({ hasBackground: value }),
  setInk: (value) => set({ inkColor: value }),
  setFont: (value) => set({ font: value }),

  reset: () => {
    const previous = get().photoUrl;
    if (previous) URL.revokeObjectURL(previous);

    set({
      photoUrl: null,
      cutoutUrl: null,
      cutoutPrintUrl: null,
      isCutting: false,
      cutFailed: false,
      name: '',
      year: '',
      lines: [''],
    });
  },
}));
